import { useMemo } from 'react';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, InputLabel } from '@mui/material';

import { defaultLanguage } from 'context';
import { useCourseContext } from 'context';
import {
  RadioFormField,
  FormChoiceResponse,
  FormChoiceResponseType,
  FORM_OTHER_OPTION_VALUE,
} from 'types';

import { Input } from '../input';
import { RadioField } from '../fields';

export const FormRadioField = ({
  id,
  label,
  options,
  translations,
}: RadioFormField) => {
  const { language } = useCourseContext();
  const { t } = useTranslation('common');
  const otherLabel = t('choiceOther');
  const [field, , helpers] = useField<FormChoiceResponse | null>(id);
  const isOtherSelected = field.value?.type === FormChoiceResponseType.OTHER;
  const controlValue = isOtherSelected
    ? FORM_OTHER_OPTION_VALUE
    : field.value?.value;

  const translation = useMemo(() => {
    if (!language || !translations || language === defaultLanguage) {
      return {
        label,
        options: options?.options
          ? [
              ...options.options.map((value) => ({ value, label: value })),
              ...(options.allowOther
                ? [{ label: otherLabel, value: FORM_OTHER_OPTION_VALUE }]
                : []),
            ]
          : [],
      };
    }

    const translation = translations[language];
    const langOptions = translation?.options || options?.options;
    return {
      label: label ? translation?.label || label : label,
      options: langOptions
        ? [
            ...langOptions.map((value, index) => ({
              label: value,
              value: options?.options?.[index] || value,
            })),
            ...(options?.allowOther
              ? [{ label: otherLabel, value: FORM_OTHER_OPTION_VALUE }]
              : []),
          ]
        : [],
    };
  }, [
    label,
    options?.options,
    options?.allowOther,
    language,
    translations,
    otherLabel,
  ]);

  return (
    <Box>
      {translation.label && (
        <InputLabel className="mb-2">{translation.label}</InputLabel>
      )}
      <RadioField
        name={id}
        padding="0px"
        displayValue={controlValue}
        options={translation.options}
        radioGroupClassName="flex flex-col gap-2"
        mapValue={(value) =>
          value === FORM_OTHER_OPTION_VALUE
            ? { value: '', type: FormChoiceResponseType.OTHER }
            : { value, type: FormChoiceResponseType.OPTION }
        }
      />
      {options?.allowOther && isOtherSelected && (
        <div className="mt-2">
          <Input
            fullWidth
            label={otherLabel}
            value={field.value?.value ?? ''}
            placeholder={t('choiceOtherPlaceholder')}
            onBlur={() => helpers.setTouched(true, true)}
            onChange={(event) => {
              helpers.setValue(
                {
                  value: event.target.value,
                  type: FormChoiceResponseType.OTHER,
                },
                true
              );
            }}
          />
        </div>
      )}
    </Box>
  );
};
